import { useEffect, useState, useCallback } from 'react';
import useSession from 'hooks/call-queue/useSession';
import useApi from 'hooks/useApi';
import useFirebase from 'hooks/useFirebase';
import { CallStatus } from 'types/auth';
import SessionEndDialog from './SessionEndDialog';
import SessionProtectionIndicator from './SessionProtectionIndicator';

/**
 * Enhanced Navigation Blocker that shows custom dialog for session management
 * Instead of relying on browser's default dialog, we intercept and show our own
 */
const EnhancedNavigationBlocker = () => {
  const { session, cleanupSession } = useSession();
  const { user, endSession, showToast } = useApi();
  const { addCallHistoryEntry, updateCallStatus } = useFirebase();
  const [showCustomDialog, setShowCustomDialog] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // Check if there's an active session that should be protected
  const isSessionActive = useCallback(() => {
    if (!session || !user) return false;
    
    const activeStatuses = [
      CallStatus.IN_PROGRESS,
      CallStatus.RECEIVED,
      CallStatus.RECEIVING,
      CallStatus.CALLING
    ];
    
    return activeStatuses.includes(session.status);
  }, [session, user]);

  // Handle ending the session
  const handleEndSession = useCallback(async () => {
    if (!session || !user) return;

    try {
      // If user is not the one who accepted the session, just leave
      if (session.supportAccepted !== user.uid) {
        addCallHistoryEntry(session.transaction!, {
          status: CallStatus.SUPPORT_LEFT,
          datetime: new Date().toISOString(),
          user: user.uid
        });

        if (cleanupSession) {
          cleanupSession();
        }

        showToast('Session ended successfully', 'success');
        return;
      }

      // End the session properly through API
      const response = await endSession(session.transaction!);
      const { success, message } = response;

      if (success) {
        addCallHistoryEntry(session.transaction!, {
          status: CallStatus.SUPPORT_LEFT,
          datetime: new Date().toISOString(),
          user: user.uid
        });

        addCallHistoryEntry(session.transaction!, {
          status: CallStatus.COMPLETED,
          datetime: new Date().toISOString(),
          user: user.uid
        });

        updateCallStatus(session.transaction!, CallStatus.COMPLETED);

        if (cleanupSession) {
          cleanupSession();
        }

        showToast('Session has been ended', 'success');
      } else {
        showToast(message || 'Failed to end session', 'error');
      }
    } catch (error: any) {
      showToast(error.message || 'Error ending session', 'error');
    }
  }, [session, user, endSession, addCallHistoryEntry, updateCallStatus, cleanupSession, showToast]);

  // Handle custom dialog confirmation
  const handleConfirmEndSession = async () => {
    setIsLoading(true);
    try {
      await handleEndSession();
      // After session ends successfully, refresh the page
      setTimeout(() => {
        window.location.reload();
      }, 500); // Small delay to ensure cleanup completes
    } catch (error) {
      console.error('Error ending session:', error);
      setIsLoading(false);
    }
  };

  // Handle custom dialog cancellation
  const handleCancel = () => {
    setShowCustomDialog(false);
    setIsLoading(false);
  };

  // Set up event listeners for various refresh attempts
  useEffect(() => {
    // Handle keyboard shortcuts (Ctrl+R, F5, Cmd+R)
    const handleKeyDown = (event: KeyboardEvent) => {
      if (!isSessionActive()) return;

      const isRefresh = (
        (event.ctrlKey || event.metaKey) && event.key === 'r'
      ) || event.key === 'F5';

      if (isRefresh) {
        event.preventDefault();
        setShowCustomDialog(true);
      }
    };

    // Handle browser refresh button and other navigation
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (!isSessionActive()) {
        return;
      }

      // Show browser's default dialog as fallback
      const message = 'You have an active session. Are you sure you want to leave?';
      event.returnValue = message;
      return message;
    };

    // Add event listeners
    document.addEventListener('keydown', handleKeyDown);
    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [isSessionActive]);

  // Debug logging
  useEffect(() => {
    if (isSessionActive()) {
      // (per-render session logging removed - pure console noise)

    }
  }, [isSessionActive, session]);

  return (
    <>
      <SessionEndDialog
        open={showCustomDialog}
        sessionType={session?.sessionType || 'CALL'}
        sessionData={{
          fromName: session?.fromName,
          key: session?.key,
          status: session?.status
        }}
        onCancel={handleCancel}
        onEndSession={handleConfirmEndSession}
        loading={isLoading}
      />
      
      <SessionProtectionIndicator
        show={isSessionActive() && !showCustomDialog}
        sessionType={session?.sessionType || 'CALL'}
        sessionData={{
          fromName: session?.fromName,
          key: session?.key
        }}
      />
    </>
  );
};

export default EnhancedNavigationBlocker;
